import { capitalizeWords } from '../../utils/functions';

/** S3 keys of the integration logos, as the legacy hook declared them. */
export const ECOMMERCE_LOGO_KEYS = {
  s3Woocommerce: 'assets/images/woocommerce.png',
  s3Shopify: 'assets/images/shopify.png',
  s3MercadoLibre: 'assets/images/mercadolibre.png',
  s3Bootic: 'assets/images/bootic.png',
  s3Prestashop: 'assets/images/prestashop.png',
  s3Vtex: 'assets/images/vtex.png',
};

/** Cache name `getImagesUrls` stores the logos under. */
export const ECOMMERCE_LOGO_CACHE = 'ecommerces';

/**
 * The logo key of an integration, derived from its name the way the legacy
 * does: "mercado libre" → `s3MercadoLibre`, "VTex" → `s3Vtex`. The backend
 * stores some names in lower case, so it has to go through `capitalizeWords`.
 *
 * @param {string} name
 * @returns {string}
 */
export const getLogoKey = (name) => `s3${capitalizeWords(name).split(' ').join('')}`;

/**
 * The integrations a seller can add: `availableEcommerces` of the legacy hook,
 * copied literally. This is the base structure the backend stores, so it must
 * not be reshaped.
 *
 * The webhook URLs read `process.env.REACT_APP_URL`, the same variable the
 * legacy used, with a trailing slash.
 *
 * @param {object} params
 * @param {number} params.sellerId
 * @param {Object.<string, string>} params.logos - From `ECOMMERCE_LOGO_KEYS`
 * @returns {Array<object>}
 */
export const buildAvailableEcommerces = ({ sellerId, logos = {} }) => [
  {
    name: "Shopify",
    type: "webhook",
    warehouse: "",
    image: logos.s3Shopify,
    tutorial_link:
      "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-shopify-webhooks.pdf",
    configuration: {
      options: {
        enabled_delivery_methods: [],
      },
      authentication: {
        shop_url: "",
        api_key: "",
        client_secret: "",
        token: "",
      },
      create_order: {
        url: `${process.env.REACT_APP_URL}shopify/create_order/${sellerId}/`,
        enabled: true,
      },
      cancel_order: {
        url: `${process.env.REACT_APP_URL}shopify/cancel_order/${sellerId}/`,
        enabled: true,
      },
      update_order: {
        url: `${process.env.REACT_APP_URL}shopify/update_order/${sellerId}/`,
        enabled: true,
      },
    },
  },
  {
    name: "Woocommerce",
    type: "webhook",
    warehouse: "",
    image: logos.s3Woocommerce,
    tutorial_link:
      "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-woocommerce-webhooks.pdf",
    configuration: {
      options: {
        enabled_delivery_methods: [],
      },
      authentication: {
        token: "",
        woocommerce_user: "",
        woocommerce_password: "",
      },
      create_order: {
        url: `${process.env.REACT_APP_URL}woocommerce/standard/create_order/${sellerId}/`,
        enabled: true,
      },
      create_order_plugin: {
        url: `${process.env.REACT_APP_URL}woocommerce/ninja_plugin/create_order/${sellerId}/`,
        enabled: true,
      },
      cancel_order: {
        url: `${process.env.REACT_APP_URL}woocommerce/cancel_order/${sellerId}/`,
        enabled: true,
      },
      update_order: {
        url: `${process.env.REACT_APP_URL}woocommerce/update_order/${sellerId}/`,
        enabled: true,
      },
    },
  },
  {
    name: "Prestashop",
    type: "webhook",
    warehouse: "",
    image: logos.s3Prestashop,
    tutorial_link:
      "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-prestashop-webhooks.pdf",
    configuration: {
      options: {
        enabled_delivery_methods: [],
      },
      authentication: {
        shop_name: "",
        shop_url: "",
        token: "",
      },
      create_order: {
        url: `${process.env.REACT_APP_URL}prestashop/create_order/${sellerId}/`,
        enabled: true,
      },
    },
  },
  {
    name: "Mercado Libre",
    type: "webhook",
    warehouse: "",
    image: logos.s3MercadoLibre,
    tutorial_link:
      "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-mercadolibre-webhooks.pdf",
    configuration: {
      authentication: {
        integration_user_id: "",
        token: "",
        refresh_token: "",
      },
      options: {
        fulfillment: false,
        xd_drop_off: false,
        cross_docking: false,
        self_service: true,
        turbo: true,
        not_specify: true,
        custom: true,
        automatic_print_label: true,
      },
      create_order: {
        url: `${process.env.REACT_APP_URL}meli/notifications/`,
        enabled: true,
      },
    },
  },
  {
    name: "Bootic",
    type: "webhook",
    warehouse: "",
    image: logos.s3Bootic,
    tutorial_link:
      "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-bootic-webhooks.pdf",
    configuration: {
      authentication: {
        token: "",
        refresh_token: "",
      },
      options: {
        enabled_delivery_methods: [],
      },
      create_order: {
        url: `${process.env.REACT_APP_URL}bootic/create_order/${sellerId}/`,
        enabled: true,
      },
      cancel_order: {
        url: `${process.env.REACT_APP_URL}bootic/cancel_order/${sellerId}/`,
        enabled: true,
      },
      update_order: {
        url: `${process.env.REACT_APP_URL}bootic/update_order/${sellerId}/`,
        enabled: true,
      },
    },
  },
  {
    name: "VTex",
    type: "webhook",
    warehouse: "",
    image: logos.s3Vtex,
    tutorial_link:
      "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-vtex-webhooks.pdf",
    configuration: {
      authentication: {
        shop_name: "",
        app_key: "",
        app_token: "",
      },
      options: {
        enabled_delivery_methods: [],
      },
      create_order: {
        url: `${process.env.REACT_APP_URL}vtex/create_order/${sellerId}`,
        enabled: true,
      },
    },
  },
];
